import {
  PkAvatar,
  PkBadge,
  PkButton,
  PkCarousel,
  PkDataCard,
  PkIdentifier,
  PkListItem,
} from '@uefadigital/panenka-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { CSSProperties } from 'styled-components';

import useIsDesktop from 'hooks/useIsDesktop';
import { routes } from 'routes';
import type { CompV2TeamPlayers, CompstatsV1TeamRanking, Ranking, Statistics } from 'types/ApiResponseInterfaces';
import { translateEntity } from 'utils/translateEntity';

const breakpoints = {
  0: { slidesPerView: 1.1, slidesPerGroup: 1, spaceBetween: 12 },
  399: { slidesPerView: 1.2, slidesPerGroup: 1, spaceBetween: 12 },
  699: { slidesPerView: 2.2, slidesPerGroup: 1, spaceBetween: 54 },
  799: { slidesPerView: 2.2, slidesPerGroup: 1, spaceBetween: 54 },
  959: { slidesPerView: 3.2, slidesPerGroup: 1, spaceBetween: 54 },
  1439: { slidesPerView: 4, slidesPerGroup: 1, spaceBetween: 54 },
} as const;

const pdfBreakpoints = {
  0: { slidesPerView: 1.5, slidesPerGroup: 1, spaceBetween: 4 },
} as const;

export interface ListItem {
  id: number;
  displayOfficialName: string;
  country: string;
  amount: number;
  avatarImage: string;
  playerName: string;
  clubCrest: string;
}

export interface Card {
  name: string;
  teamStats: CompstatsV1TeamRanking[];
  playerStats: Ranking[];
  statistics: string;
  teamForPlayers: CompV2TeamPlayers[];
  isError: boolean;
}

export interface CardProperty {
  cards: Card[];
  title?: string;
  seeAllButtonTitle?: string;
  smallMarginHorizontal?: boolean;
  isPDFPrint?: boolean;
}

export const TournamentStats = ({
  cards,
  title,
  seeAllButtonTitle,
  smallMarginHorizontal,
  isPDFPrint = false,
}: CardProperty) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isDesktop = useIsDesktop() || isPDFPrint;

  if (!cards.length) {
    return <div />;
  }

  const Title = isDesktop
    ? 'pk-font-size--xl3 pk-font-weight--bolder pk-line-height--sm pk-font-style--normal pr-font-size--14'
    : 'pk-font-size--xl2 pk-font-weight--bolder pk-line-height--sm pk-font-style--normal';

  const getAmountOf = (statistics: Statistics[], type: string) => {
    const amount = statistics.filter(function (item) {
      return item.name === type;
    });

    if (!amount || !amount[0]) {
      return 0;
    }
    return Number(amount[0].value);
  };

  const getListItemsForTeam = (teams: CompstatsV1TeamRanking[], type: string) => {
    const statisticList: ListItem[] = [];

    teams.forEach((teamItem, index) => {
      statisticList.push({
        id: ++index,
        displayOfficialName: translateEntity(teamItem?.team)?.displayOfficialName ?? '',
        country: translateEntity(teamItem?.team)?.countryName ?? '',
        amount: getAmountOf(teamItem.statistics, type),
        avatarImage: '',
        playerName: '',
        clubCrest: teamItem?.team?.logoUrl ?? '',
      });
    });

    return statisticList;
  };

  const getListItemsForPlayer = (players: Ranking[], type: string, teamForPlayers: CompV2TeamPlayers[]) => {
    const statisticList: ListItem[] = [];

    players.forEach((playerItem, index) => {
      const team = teamForPlayers.filter(function (item: CompV2TeamPlayers) {
        return item.playerId === playerItem.player.id;
      });

      statisticList.push({
        id: ++index,
        displayOfficialName: translateEntity(team[0]?.team)?.displayOfficialName ?? '',
        country: translateEntity(playerItem?.player)?.countryName ?? '',
        amount: getAmountOf(playerItem.statistics, type),
        avatarImage: playerItem?.player?.imageUrl ?? '',
        playerName: translateEntity(playerItem?.player)?.name ?? '',
        clubCrest: team[0]?.team?.logoUrl ?? '',
      });
    });

    return statisticList;
  };

  const getAvatarWithBadge = (data: ListItem) => {
    return (
      <PkAvatar fallback-image="generic-head" type="image" size={isPDFPrint ? 40 : 50} src={data.avatarImage}>
        <PkBadge
          alt={data.displayOfficialName}
          slot="badge"
          badgeTitle={data.displayOfficialName}
          size={isPDFPrint ? 15 : 20}
          fallbackImage="club-generic-badge"
          src={data.clubCrest}
        />
      </PkAvatar>
    );
  };

  const getClubBadge = (data: ListItem) => {
    return (
      <PkBadge
        alt={data.displayOfficialName}
        slot="badge"
        badgeTitle={data.displayOfficialName}
        fallbackImage="club-generic-badge"
        src={data.clubCrest}
      />
    );
  };

  const getPkListItem = (element: ListItem, isPlayer = false, isHeader = false) => {
    return (
      <PkListItem has-divider="false" className="pk-my--xs pk-my-md--s">
        <span slot="prefix-card" className={`pr-font-size--10 ${isHeader ? 'pk-text--text-04' : ''}`}>
          {' '}
          {element.id}{' '}
        </span>
        <PkIdentifier>
          <span slot="prefix" className="pk-mr--xs">
            {isPlayer ? getAvatarWithBadge(element) : getClubBadge(element)}
          </span>
          <span slot="primary" className={`pr-font-size--8 ${isHeader ? 'pk-text--text-04' : ''}`}>
            {isPlayer ? element.playerName : element.displayOfficialName}
          </span>
          <span slot="secondary" className={`pr-font-size--8 ${isHeader ? 'pk-text--text-04' : ''}`}>
            {isPlayer ? element.displayOfficialName : element.country}
          </span>
        </PkIdentifier>
        <div slot="suffix-card">
          <div
            className={`d-flex justify-content-end pr-font-size--10 ${
              isHeader ? 'pk-text--text-04 pr-title--white' : ''
            } `}
          >
            <span>{element.amount}</span>
          </div>
        </div>
      </PkListItem>
    );
  };

  const cssPropertiesPrimary: CSSProperties = smallMarginHorizontal
    ? ({
        ['--pk-carousel--margin-horizontal']: 'var(--pk-spacing-xs3)',
        ['--pk-carousel--margin-horizontal-mobile']: 'var(--pk-spacing-xs3)',
        ['--pk-carousel--container-margin-top']: 'var(--pk-spacing-xs3)',
      } as CSSProperties)
    : {};

  const carouselBreakpoints = isPDFPrint ? pdfBreakpoints : breakpoints;
  const squadDetailsRedirect = () => {
    const link = `${location.pathname}/${routes.squadDetails}`.replace(/\/{2,}/g, '/');

    navigate(link);
  };

  return (
    <div className="pk-pr--0">
      <PkCarousel breakpoints={carouselBreakpoints} box-shadow-padding={false} style={cssPropertiesPrimary}>
        <div slot="headline" className={`${Title} pk-text--text-01`}>
          {title}
        </div>
        {cards.map((card) => {
          let data;
          const isPlayer = card.playerStats.length > 0;
          const isTeam = card.teamStats.length > 0;

          if (isPlayer) {
            data = getListItemsForPlayer(card.playerStats, card.statistics, card.teamForPlayers);
          } else if (isTeam) {
            data = getListItemsForTeam(card.teamStats, card.statistics);
          } else {
            return;
          }

          return (
            <PkDataCard key={card.name} bg-color="secondary-01" className="card-shadow">
              <div slot="header">
                <div className="pk-text--text-04 pk-mb-20 pr-font-size--14">{t(card.name)}</div>
                {card.isError ? '' : getPkListItem(data[0], isPlayer, true)}
              </div>
              <div slot="body">
                {card.isError ? (
                  <div>{t('error')}</div>
                ) : (
                  data.slice(1).map((statisticItem) => {
                    return getPkListItem(statisticItem, isPlayer);
                  })
                )}
              </div>
            </PkDataCard>
          );
        })}
        {seeAllButtonTitle ? (
          <PkButton
            slot="cta"
            onPkClick={squadDetailsRedirect}
            appearance="outlined"
            className="pk-mt--xs pr-display--none"
          >
            {' '}
            {seeAllButtonTitle}
          </PkButton>
        ) : (
          ''
        )}
      </PkCarousel>
    </div>
  );
};
